import { Container, Title, Text, Stack, List, Anchor } from '@mantine/core';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks';

export function Terms() {
  useSEO({
    title: 'Regulamin',
    description: 'Regulamin serwisu Taryfy Pradu. Zasady korzystania z platformy, prawa i obowiązki użytkowników.',
    canonicalPath: '/terms',
  });
  return (
    <Layout isAuthenticated={false}>
      <Container size="md" py={80}>
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Title order={1} mb="md">
              Regulamin Serwisu
            </Title>
            <Text c="dimmed" mb="xl">
              Ostatnia aktualizacja: 5 stycznia 2026
            </Text>
            <Text size="lg" c="dimmed">
              Niniejszy Regulamin określa zasady korzystania z platformy Taryfy Prądu, prawa
              i obowiązki użytkowników oraz warunki świadczenia usług.
            </Text>
          </div>

          {/* Definitions */}
          <div>
            <Title order={2} size="h3" mb="md">
              1. Definicje
            </Title>
            <List spacing="sm" c="dimmed">
              <List.Item>
                <strong>Serwis/Platforma:</strong> platforma internetowa Taryfy Prądu dostępna
                pod adresem taryfypradu.pl
              </List.Item>
              <List.Item>
                <strong>Usługodawca/Administrator:</strong> Robert Boguszewski, ul. Królowej Jadwigi 34a,
                32-040 Świątniki Górne, NIP: 944-203-67-90, REGON: 362403341
              </List.Item>
              <List.Item>
                <strong>Użytkownik:</strong> osoba fizyczna korzystająca z Serwisu, w tym
                użytkownicy zarejestrowani oraz korzystający w trybie gościa
              </List.Item>
              <List.Item>
                <strong>Konto:</strong> indywidualne konto użytkownika utworzone w Serwisie
              </List.Item>
              <List.Item>
                <strong>Usługi:</strong> funkcjonalności oferowane przez Serwis, w tym kalkulator
                taryf energetycznych i zapisywanie obliczeń
              </List.Item>
            </List>
          </div>

          {/* General Provisions */}
          <div>
            <Title order={2} size="h3" mb="md">
              2. Postanowienia ogólne
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                2.1. Niniejszy Regulamin określa zasady korzystania z Serwisu, prawa i obowiązki
                Użytkowników oraz Usługodawcy.
              </Text>
              <Text c="dimmed">
                2.2. Korzystanie z Serwisu oznacza akceptację niniejszego Regulaminu.
              </Text>
              <Text c="dimmed">
                2.3. Użytkownik zobowiązany jest do zapoznania się z treścią Regulaminu przed
                rozpoczęciem korzystania z Serwisu.
              </Text>
              <Text c="dimmed">
                2.4. Usługodawca zastrzega sobie prawo do zmiany Regulaminu. O zmianach Użytkownicy
                zostaną poinformowani z 7-dniowym wyprzedzeniem.
              </Text>
            </Stack>
          </div>

          {/* Services */}
          <div>
            <Title order={2} size="h3" mb="md">
              3. Zakres usług
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                3.1. Serwis udostępnia następujące usługi:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>
                  Kalkulator porównujący koszty różnych taryf energetycznych
                </List.Item>
                <List.Item>
                  Możliwość tworzenia konta i zapisywania wyników obliczeń (dla zarejestrowanych
                  użytkowników)
                </List.Item>
                <List.Item>
                  Dostęp do aktualnych cenników energii elektrycznej w Polsce
                </List.Item>
              </List>
              <Text c="dimmed">
                3.2. Wszystkie usługi świadczone są bezpłatnie dla użytkowników indywidualnych.
              </Text>
              <Text c="dimmed">
                3.3. Usługodawca nie gwarantuje nieprzerwanej dostępności Serwisu i zastrzega
                sobie prawo do okresowych prac konserwacyjnych.
              </Text>
              <Text c="dimmed">
                3.4. Serwis umożliwia eksport danych kalkulacji do pliku CSV (Comma-Separated Values).
                Funkcja eksportu służy realizacji prawa Użytkownika do przenoszenia danych zgodnie
                z art. 20 RODO. Eksportowane pliki CSV zawierają szczegółowe dane zużycia energii
                i wyniki porównań taryf w formacie możliwym do wykorzystania w programach arkuszy
                kalkulacyjnych (Excel, Google Sheets). Eksport odbywa się lokalnie w przeglądarce
                Użytkownika - dane nie są przesyłane na serwer podczas generowania pliku.
              </Text>
            </Stack>
          </div>

          {/* Account Registration */}
          <div>
            <Title order={2} size="h3" mb="md">
              4. Rejestracja i konto użytkownika
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                4.1. Rejestracja konta jest dobrowolna, ale konieczna do korzystania z pełnej
                funkcjonalności Serwisu.
              </Text>
              <Text c="dimmed">
                4.2. Aby utworzyć konto, Użytkownik musi:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>Mieć ukończone 16 lat</List.Item>
                <List.Item>Podać aktualny adres email</List.Item>
                <List.Item>Utworzyć bezpieczne hasło</List.Item>
                <List.Item>Zaakceptować niniejszy Regulamin</List.Item>
              </List>
              <Text c="dimmed">
                4.3. Użytkownik zobowiązuje się do:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>Podawania prawdziwych danych</List.Item>
                <List.Item>Zachowania poufności hasła do konta</List.Item>
                <List.Item>Niezwłocznego powiadomienia o nieupoważnionym dostępie do konta</List.Item>
                <List.Item>Korzystania z Serwisu zgodnie z prawem i Regulaminem</List.Item>
              </List>
              <Text c="dimmed">
                4.4. Użytkownik może w każdej chwili usunąć swoje konto, kontaktując się
                z Usługodawcą.
              </Text>
            </Stack>
          </div>

          {/* User Obligations */}
          <div>
            <Title order={2} size="h3" mb="md">
              5. Obowiązki użytkownika
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                5.1. Użytkownik zobowiązuje się do korzystania z Serwisu w sposób zgodny z prawem,
                Regulaminem oraz dobrymi obyczajami.
              </Text>
              <Text c="dimmed">
                5.2. Zabrania się:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>
                  Podawania fałszywych, wprowadzających w błąd lub nielegalnych treści
                </List.Item>
                <List.Item>
                  Wykorzystywania Serwisu w sposób mogący zakłócić jego funkcjonowanie
                </List.Item>
                <List.Item>
                  Prób nieuprawnionego dostępu do systemów lub danych innych użytkowników
                </List.Item>
                <List.Item>
                  Kopiowania, modyfikowania lub rozpowszechniania treści Serwisu bez zgody
                </List.Item>
                <List.Item>
                  Wykorzystywania Serwisu do celów komercyjnych bez zgody Usługodawcy
                </List.Item>
              </List>
            </Stack>
          </div>

          {/* Data Accuracy Disclaimer */}
          <div>
            <Title order={2} size="h3" mb="md">
              6. Dokładność danych i wyłączenie odpowiedzialności
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                6.1. <strong>Brak gwarancji dokładności:</strong> Usługodawca <strong>NIE GWARANTUJE</strong> dokładności,
                aktualności ani kompletności prezentowanych cen energii i wyników obliczeń. Wszystkie dane
                mają charakter wyłącznie <strong>informacyjny i szacunkowy</strong>.
              </Text>
              <Text c="dimmed">
                6.2. <strong>Obowiązek weryfikacji użytkownika:</strong> Użytkownik jest <strong>zobowiązany</strong> do
                samodzielnej weryfikacji wszystkich danych cenowych i wyników obliczeń bezpośrednio u swojego
                dostawcy energii elektrycznej oraz na stronie Urzędu Regulacji Energetyki (URE) przed
                podjęciem jakichkolwiek decyzji dotyczących zmiany taryfy lub umowy.
              </Text>
              <Text c="dimmed">
                6.3. <strong>WAŻNE: Ceny rynkowe od 1.01.2026:</strong> Od 1 stycznia 2026 roku obowiązują
                nowe ceny rynkowe energii elektrycznej zatwierdzone przez URE. Zamrożenie cen, które
                obowiązywało do 31 grudnia 2025 roku, zakończyło się. Wszystkie kalkulacje w Serwisie
                są aktualizowane zgodnie z nowymi taryfami na rok 2026. Użytkownik ponosi pełną
                odpowiedzialność za monitorowanie ewentualnych przyszłych zmian cen.
              </Text>
              <Text c="dimmed">
                6.4. Dane cenowe pochodzą z oficjalnych taryf operatorów sieci dystrybucyjnych
                zatwierdzonych przez URE, jednak Usługodawca nie ponosi odpowiedzialności za:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>Błędy w danych źródłowych dostarczanych przez dystrybutorów</List.Item>
                <List.Item>Opóźnienia w aktualizacji cen w przypadku zmian taryfowych</List.Item>
                <List.Item>
                  Różnice wynikające z indywidualnych warunków umów z dostawcami energii
                </List.Item>
                <List.Item>
                  Zmiany cen energii lub opłat dystrybucyjnych wprowadzone przez dostawców lub URE
                </List.Item>
                <List.Item>
                  Niedokładności wynikające z błędnych danych wprowadzonych przez Użytkownika
                </List.Item>
              </List>
              <Text c="dimmed">
                6.5. <strong>Charakter rekomendacji:</strong> Wszelkie rekomendacje, sugestie lub
                porównania taryf przedstawione w Serwisie są <strong>niewiążące</strong> i mają charakter
                wyłącznie orientacyjny. Ostateczne decyzje dotyczące wyboru taryfy energetycznej należą
                wyłącznie do Użytkownika.
              </Text>
              <Text c="dimmed">
                6.6. Usługodawca <strong>nie ponosi odpowiedzialności</strong> za jakiekolwiek decyzje
                Użytkownika podjęte na podstawie wyników z kalkulatora, rekomendacji lub danych
                prezentowanych w Serwisie, w tym za ewentualne straty finansowe wynikające z takich decyzji.
              </Text>
              <Text c="dimmed">
                6.7. Dane eksportowane do plików CSV są generowane na podstawie bieżących obliczeń
                i danych wprowadzonych przez Użytkownika. Usługodawca nie gwarantuje dokładności
                danych w plikach CSV w przypadku błędnych danych wejściowych, przestarzałych cenników
                lub błędów w obliczeniach. Użytkownik <strong>musi zweryfikować</strong> poprawność danych
                przed wykorzystaniem pliku CSV do celów decyzyjnych lub analitycznych.
              </Text>
            </Stack>
          </div>

          {/* Liability Limitation */}
          <div>
            <Title order={2} size="h3" mb="md">
              7. Ograniczenie odpowiedzialności
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                7.1. Usługodawca nie ponosi odpowiedzialności za:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>
                  Szkody wynikające z przerw w dostępności Serwisu spowodowanych awariami
                  technicznymi, pracami konserwacyjnymi lub siłą wyższą
                </List.Item>
                <List.Item>
                  Utratę danych wynikającą z działania Użytkownika lub osób trzecich
                </List.Item>
                <List.Item>
                  Nieuprawniony dostęp osób trzecich do konta Użytkownika wynikający z ujawnienia
                  hasła przez Użytkownika
                </List.Item>
                <List.Item>
                  Szkody wynikające z nieprawidłowego wprowadzenia danych przez Użytkownika
                </List.Item>
              </List>
              <Text c="dimmed">
                7.2. Serwis jest świadczony "takim, jakim jest" (as-is) bez jakichkolwiek gwarancji.
              </Text>
              <Text c="dimmed">
                7.3. W maksymalnym zakresie dopuszczonym przez prawo odpowiedzialność Usługodawcy
                jest ograniczona do szkód bezpośrednich rzeczywiście poniesionych przez Użytkownika.
              </Text>
              <Text c="dimmed">
                7.4. Usługodawca nie ponosi odpowiedzialności za utratę, ujawnienie, modyfikację
                lub nieuprawniony dostęp do plików CSV pobranych przez Użytkownika. Po pobraniu
                pliku CSV na urządzenie Użytkownika, odpowiedzialność za bezpieczeństwo tego pliku
                spoczywa wyłącznie na Użytkowniku. Usługodawca nie ma kontroli nad tym, w jaki sposób
                Użytkownik przechowuje, wykorzystuje lub udostępnia pobrane pliki CSV.
              </Text>
            </Stack>
          </div>

          {/* Intellectual Property */}
          <div>
            <Title order={2} size="h3" mb="md">
              8. Własność intelektualna
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                8.1. Wszystkie prawa własności intelektualnej do Serwisu, w tym prawa autorskie,
                znaki towarowe, układ graficzny, kod źródłowy, należą do Usługodawcy.
              </Text>
              <Text c="dimmed">
                8.2. Użytkownik nie może kopiować, modyfikować, rozpowszechniać ani wykorzystywać
                komercyjnie treści Serwisu bez pisemnej zgody Usługodawcy.
              </Text>
              <Text c="dimmed">
                8.3. Pliki CSV wygenerowane przez Użytkownika zawierają dane wprowadzone przez Użytkownika
                oraz wyniki obliczeń wygenerowane przez Serwis. Użytkownik ma prawo do wykorzystywania
                pobranych plików CSV w dowolny sposób, w tym do celów osobistych, edukacyjnych
                i komercyjnych, z zastrzeżeniem obowiązku zachowania odpowiednich ostrzeżeń
                i wyłączeń odpowiedzialności Usługodawcy. Użytkownik nie może rozpowszechniać plików CSV
                w sposób sugerujący, że pochodzą bezpośrednio z oficjalnych źródeł dostawców energii.
              </Text>
              <Text c="dimmed">
                8.4. Po pobraniu pliku CSV Użytkownik staje się jego wyłącznym posiadaczem
                i ponosi pełną odpowiedzialność za sposób jego wykorzystania i przechowywania.
                Usługodawca zaleca zabezpieczenie plików CSV przed nieuprawnionym dostępem,
                ponieważ zawierają one dane osobowe Użytkownika. Użytkownik zobowiązuje się do
                nieprzekazywania plików CSV osobom trzecim w sposób naruszający ich prywatność
                lub prawa własności intelektualnej Usługodawcy.
              </Text>
              <Text c="dimmed">
                8.5. Dane wprowadzone przez Użytkownika (wyniki obliczeń, preferencje) pozostają
                własnością Użytkownika.
              </Text>
              <Text c="dimmed" mt="md" fw={600}>
                8.6. Znaki towarowe i nazwy własne
              </Text>
              <Text c="dimmed">
                Wszystkie wymienione na stronie znaki towarowe, znaki handlowe, logo, nazwy firm
                oraz nazwy produktów są własnością ich prawnych właścicieli i są używane wyłącznie
                w celach informacyjnych. Użycie tych oznaczeń nie stanowi naruszenia praw własności
                intelektualnej i służy jedynie identyfikacji dostawców energii, taryf oraz
                usług energetycznych w ramach porównania cen. Usługodawca nie posiada żadnych
                praw do tych znaków i nie utrzymuje z ich właścicielami żadnych relacji biznesowych,
                chyba że wyraźnie wskazano inaczej.
              </Text>
            </Stack>
          </div>

          {/* Data Protection */}
          <div>
            <Title order={2} size="h3" mb="md">
              9. Ochrona danych osobowych
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                9.1. Zasady przetwarzania danych osobowych określa{' '}
                <Anchor href="/privacy-policy">Polityka Prywatności</Anchor>, która stanowi
                integralną część niniejszego Regulaminu.
              </Text>
              <Text c="dimmed">
                9.2. Użytkownik, rejestrując konto, wyraża zgodę na przetwarzanie danych osobowych
                zgodnie z Polityką Prywatności.
              </Text>
              <Text c="dimmed">
                9.3. Usługodawca nie przechowuje haseł do systemów dostawców energii (np. eLicznik).
                Wszelkie połączenia z zewnętrznymi systemami są przetwarzane w pamięci
                i natychmiast usuwane.
              </Text>
            </Stack>
          </div>

          {/* Account Termination */}
          <div>
            <Title order={2} size="h3" mb="md">
              10. Rozwiązanie umowy i usunięcie konta
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                10.1. Użytkownik może w każdej chwili usunąć swoje konto, kontaktując się
                z Usługodawcą na adres{' '}
                <Anchor href="mailto:kontakt@taryfypradu.pl">kontakt@taryfypradu.pl</Anchor>.
                Usługodawca usunie konto w ciągu 30 dni od otrzymania żądania, z zastrzeżeniem
                obowiązków prawnych wynikających z przepisów prawa (np. obowiązku przechowywania
                danych księgowych).
              </Text>
              <Text c="dimmed">
                10.2. Usługodawca zastrzega sobie prawo do zawieszenia lub usunięcia konta
                Użytkownika w przypadku:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>Naruszenia postanowień Regulaminu</List.Item>
                <List.Item>Działań niezgodnych z prawem</List.Item>
                <List.Item>Próby zakłócenia funkcjonowania Serwisu</List.Item>
                <List.Item>Braku aktywności przez okres dłuższy niż 24 miesiące</List.Item>
              </List>
              <Text c="dimmed">
                10.3. Po usunięciu konta dane Użytkownika zostaną usunięte zgodnie z Polityką
                Prywatności, z zastrzeżeniem obowiązków wynikających z przepisów prawa.
              </Text>
            </Stack>
          </div>

          {/* Consumer Rights */}
          <div>
            <Title order={2} size="h3" mb="md">
              11. Prawa konsumenta
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                11.1. <strong>Prawo do składania reklamacji:</strong> Użytkownik będący konsumentem
                ma prawo do składania reklamacji w przypadku nieprawidłowego świadczenia usług.
                Reklamacje można składać na adres email:{' '}
                <Anchor href="mailto:kontakt@taryfypradu.pl">kontakt@taryfypradu.pl</Anchor>.
              </Text>
              <Text c="dimmed">
                11.2. <strong>Termin rozpatrzenia reklamacji:</strong> Reklamacje rozpatrywane są
                w terminie 14 dni roboczych od daty otrzymania. Odpowiedź na reklamację zostanie
                wysłana na adres email wskazany w reklamacji lub przypisany do konta użytkownika.
              </Text>
              <Text c="dimmed">
                11.3. <strong>Prawo odstąpienia od umowy:</strong> Ze względu na charakter usługi
                (usługa elektroniczna świadczona nieodpłatnie), prawo odstąpienia od umowy nie ma
                zastosowania zgodnie z art. 27 ust. 1 pkt 12 ustawy o prawach konsumenta. Użytkownik
                może w każdej chwili zaprzestać korzystania z Serwisu lub usunąć swoje konto.
              </Text>
              <Text c="dimmed">
                11.4. <strong>Pozasądowe rozstrzyganie sporów:</strong> Informacje o możliwościach
                pozasądowego rozstrzygania sporów znajdują się w punkcie 12 Regulaminu (Rozstrzyganie
                sporów i ODR).
              </Text>
            </Stack>
          </div>

          {/* Changes to Terms */}
          <div>
            <Title order={2} size="h3" mb="md">
              12. Zmiany Regulaminu
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                12.1. Usługodawca zastrzega sobie prawo do wprowadzania zmian w Regulaminie.
              </Text>
              <Text c="dimmed">
                12.2. O planowanych zmianach Użytkownicy zostaną poinformowani z co najmniej
                7-dniowym wyprzedzeniem poprzez:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>Wiadomość email na adres przypisany do konta</List.Item>
                <List.Item>Komunikat na stronie głównej Serwisu</List.Item>
              </List>
              <Text c="dimmed">
                12.3. Kontynuowanie korzystania z Serwisu po wejściu w życie zmian oznacza
                akceptację nowego Regulaminu.
              </Text>
              <Text c="dimmed">
                12.4. Użytkownik, który nie zgadza się ze zmianami, ma prawo do usunięcia konta
                przed wejściem w życie nowego Regulaminu.
              </Text>
            </Stack>
          </div>

          {/* Dispute Resolution */}
          <div>
            <Title order={2} size="h3" mb="md">
              13. Rozstrzyganie sporów i ODR
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                13.1. Wszelkie spory wynikające z korzystania z Serwisu będą rozstrzygane
                polubownie.
              </Text>
              <Text c="dimmed">
                13.2. W przypadku braku możliwości polubownego rozstrzygnięcia, spory będą
                rozstrzygane przez sąd właściwy zgodnie z przepisami prawa polskiego.
              </Text>
              <Text c="dimmed">
                13.3. Konsument może skorzystać z pozasądowych sposobów rozpatrywania reklamacji
                i dochodzenia roszczeń, w tym przez:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>Złożenie skargi do Rzecznika Praw Konsumenta</List.Item>
                <List.Item>
                  Złożenie wniosku o mediację do stałego polubownego sądu konsumenckiego
                </List.Item>
                <List.Item>
                  Skorzystanie z pomocy powiatowego lub miejskiego rzecznika konsumentów
                </List.Item>
              </List>
              <Text c="dimmed" mt="md" fw={600}>
                13.4. Platforma internetowa ODR (Online Dispute Resolution)
              </Text>
              <Text c="dimmed">
                Zgodnie z Rozporządzeniem Parlamentu Europejskiego i Rady (UE) nr 524/2013,
                informujemy o możliwości skorzystania z platformy internetowego systemu
                rozstrzygania sporów między konsumentami i przedsiębiorcami na szczeblu
                unijnym (platforma ODR).
              </Text>
              <Text c="dimmed" mt="xs">
                Platforma ODR dostępna jest pod adresem:{' '}
                <Anchor
                  href="https://ec.europa.eu/consumers/odr"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://ec.europa.eu/consumers/odr
                </Anchor>
              </Text>
              <Text c="dimmed" mt="xs">
                Nasz adres email: kontakt@taryfypradu.pl
              </Text>
            </Stack>
          </div>

          {/* Final Provisions */}
          <div>
            <Title order={2} size="h3" mb="md">
              14. Postanowienia końcowe
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                14.1. W sprawach nieuregulowanych niniejszym Regulaminem stosuje się przepisy
                prawa polskiego.
              </Text>
              <Text c="dimmed">
                14.2. Jeśli jakiekolwiek postanowienie Regulaminu zostanie uznane za nieważne,
                pozostałe postanowienia pozostają w mocy.
              </Text>
              <Text c="dimmed">
                14.3. Niniejszy Regulamin wchodzi w życie z dniem 3 listopada 2025 r.
              </Text>
            </Stack>
          </div>

          {/* Platform Independence */}
          <div>
            <Title order={2} size="h3" mb="md">
              15. Niezależność Platformy i Znaki Towarowe
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                15.1. <strong>Niezależność:</strong> Taryfy Prądu jest niezależną platformą
                porównawczą. Nie jesteśmy powiązani, sponsorowani ani zatwierdzeni przez
                TAURON® Dystrybucja S.A., TAURON® Sprzedaż sp. z o.o., PGE® Dystrybucja S.A.,
                ENEA® Operator Sp. z o.o., ENERGA®-OPERATOR S.A. ani żadnych innych
                dostawców energii elektrycznej, operatorów sieci dystrybucyjnej lub spółek
                obrotu energią w Polsce.
              </Text>
              <Text c="dimmed">
                15.2. <strong>Znaki towarowe:</strong> Wszelkie znaki towarowe, logo, nazwy
                firm i znaki usługowe użyte w Serwisie są własnością ich prawnych
                właścicieli. W szczególności nazwy TAURON®, PGE®, ENEA®, ENERGA® są
                zastrzeżonymi znakami towarowymi odpowiednich spółek. Użycie tych znaków
                ma charakter wyłącznie informacyjny w ramach porównania taryf energetycznych
                i nie oznacza żadnego powiązania, sponsoringu ani rekomendacji ze strony
                właścicieli tych znaków.
              </Text>
              <Text c="dimmed">
                15.3. <strong>Charakter usługi:</strong> Serwis świadczy wyłącznie usługi
                informacyjne i porównawcze. Nie sprzedajemy energii elektrycznej, nie
                zawieramy umów w imieniu dostawców energii i nie pośredniczymy w wyborze
                taryfy. Użytkownik samodzielnie kontaktuje się z wybranym dostawcą energii.
              </Text>
              <Text c="dimmed">
                15.4. <strong>Dane cenowe:</strong> Informacje o cenach energii i taryfach
                pochodzą z publicznie dostępnych źródeł (taryfy zatwierdzone przez URE).
                Nie otrzymujemy wynagrodzenia od dostawców energii za wyświetlanie ich
                ofert ani za kierowanie do nich użytkowników.
              </Text>
            </Stack>
          </div>

          {/* Contact */}
          <div>
            <Title order={2} size="h3" mb="md">
              16. Kontakt
            </Title>
            <Text c="dimmed" mb="sm">
              W sprawach związanych z niniejszym Regulaminem prosimy o kontakt:
            </Text>
            <Stack gap="xs">
              <Text c="dimmed">
                <strong>Administrator:</strong> Robert Boguszewski
              </Text>
              <Text c="dimmed">
                <strong>Adres:</strong> ul. Królowej Jadwigi 34a, 32-040 Świątniki Górne
              </Text>
              <Text c="dimmed">
                <strong>NIP:</strong> 944-203-67-90
              </Text>
              <Text c="dimmed">
                <strong>REGON:</strong> 362403341
              </Text>
              <Text c="dimmed" mt="sm">
                <strong>Email - sprawy ogólne:</strong>{' '}
                <Anchor href="mailto:kontakt@taryfypradu.pl">kontakt@taryfypradu.pl</Anchor>
              </Text>
              <Text c="dimmed">
                <strong>Email - pomoc techniczna:</strong>{' '}
                <Anchor href="mailto:pomoc@taryfypradu.pl">pomoc@taryfypradu.pl</Anchor>
              </Text>
              <Text c="dimmed">
                <strong>Email - RODO i prywatność:</strong>{' '}
                <Anchor href="mailto:rodo@taryfypradu.pl">rodo@taryfypradu.pl</Anchor>
              </Text>
            </Stack>
          </div>

          {/* Acceptance Notice */}
          <div style={{
            backgroundColor: 'var(--mantine-color-ecoGreen-0)',
            padding: '1.5rem',
            borderRadius: '8px',
            borderLeft: '4px solid var(--mantine-color-ecoGreen-6)'
          }}>
            <Text fw={600} mb="xs">
              Akceptacja Regulaminu
            </Text>
            <Text size="sm" c="dimmed">
              Korzystając z platformy Taryfy Prądu, potwierdzasz, że zapoznałeś się z niniejszym
              Regulaminem i akceptujesz jego postanowienia. Jeśli nie akceptujesz Regulaminu,
              nie możesz korzystać z Serwisu.
            </Text>
          </div>
        </Stack>
      </Container>
    </Layout>
  );
}
